/** Resizable splitter component (horizontal column or vertical row). */

import { useCallback, useEffect, useRef } from "react";

interface Props {
  direction: "horizontal" | "vertical";
  onResize: (delta: number) => void;
}

export function Splitter({ direction, onResize }: Props): JSX.Element {
  const startRef = useRef<number | null>(null);
  const dragging = useRef(false);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    startRef.current = direction === "horizontal" ? e.clientX : e.clientY;
    document.body.style.cursor =
      direction === "horizontal" ? "col-resize" : "row-resize";
  }, [direction]);

  useEffect(() => {
    const move = (e: MouseEvent): void => {
      if (!dragging.current || startRef.current === null) return;
      const current = direction === "horizontal" ? e.clientX : e.clientY;
      onResize(current - startRef.current);
      startRef.current = current;
    };
    const up = (): void => {
      dragging.current = false;
      startRef.current = null;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [direction, onResize]);

  return (
    <div
      className={`splitter${direction === "vertical" ? " row" : ""}`}
      onMouseDown={onMouseDown}
    />
  );
}
